import { Form, type FormInstance } from 'antd';
import { type ReactNode, useEffect, useMemo } from 'react';

import { Badge } from '@/components/Badge/Badge';
import {
  INTEGRATION_STATUS,
  IntegrationStatusMap,
} from '@/features/Setting/Integrations/types';
import { useKyobo } from '@/hooks/domain/customWorkspace/useKyobo/useKyobo';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import {
  getFeedbackSourceName,
  getSourceIcon,
  isScrapingSourceType,
  type SOURCE_TYPE,
} from '@/types/feedbackConst';

import {
  getScraperOptionName,
  getScraperOptionsBySource,
} from '../configs/config-actions';
import type { DraftScrapingJob, SCRAPER_NAME } from '../types';

export const useInitialSource = (
  form: FormInstance<DraftScrapingJob>,
): {
  sourceOptions: { label: ReactNode; value: SOURCE_TYPE }[];
  currentSourceType?: SOURCE_TYPE;
  scraperOptions: { label: ReactNode; value: SCRAPER_NAME }[];
  currentScraperOption?: SCRAPER_NAME;
  integrationStatus?: INTEGRATION_STATUS;
} => {
  const { filterKyoboOnlyScraperOptions } = useKyobo();
  const currentSourceType = Form.useWatch('source_type', form);
  const currentScraperOption = Form.useWatch('scraper_name', form);

  const sourceInfo = useFeedbackSourceStore((state) => state.sourceInfo);
  const sourceOptions = useMemo(() => {
    return sourceInfo
      ?.filter((source) => isScrapingSourceType(source.source))
      .filter((source) => source.status !== INTEGRATION_STATUS.DISABLE)
      .sort((a, b) =>
        getFeedbackSourceName(a.source).localeCompare(
          getFeedbackSourceName(b.source),
        ),
      )
      .map((item) => {
        return {
          label: (
            <div className="flex items-center gap-1.5">
              {getSourceIcon(item.source)}
              {getFeedbackSourceName(item.source)}
              {item.status !== INTEGRATION_STATUS.HEALTHY && (
                <Badge variant="warning">
                  {IntegrationStatusMap.get(item.status)}
                </Badge>
              )}
            </div>
          ),
          value: item.source,
        };
      });
  }, [sourceInfo]);

  const scraperOptions = useMemo(() => {
    if (!currentSourceType) return [];
    return filterKyoboOnlyScraperOptions(
      getScraperOptionsBySource(currentSourceType),
    ).map((item) => {
      const label = getScraperOptionName(item);
      return {
        label,
        value: item,
      };
    });
  }, [currentSourceType, filterKyoboOnlyScraperOptions]);

  useEffect(() => {
    if (!currentSourceType) return;
    const scraperOptions = getScraperOptionsBySource(currentSourceType);
    if (scraperOptions.length === 0) {
      throw new Error('Scraper options is not found');
    }
    if (scraperOptions.length === 1) {
      form.setFieldsValue({
        scraper_name: scraperOptions[0],
      });
    } else {
      form.setFieldsValue({
        scraper_name: undefined,
      });
    }
  }, [currentSourceType]);

  return {
    sourceOptions: sourceOptions || [],
    currentSourceType,
    scraperOptions,
    currentScraperOption,
    integrationStatus: sourceInfo?.find(
      (source) => source.source === currentSourceType,
    )?.status,
  };
};
